/**
 * Imported Template Store
 *
 * Session-scoped registry of AutoTemplates imported through the Templates
 * gallery "Import from JSON" card. Imported templates are converted to
 * `TemplateDefinition` via the template adapter and merged into the gallery by
 * `useTemplates`, so they preview and apply exactly like storage templates.
 *
 * Entries live in memory only — they survive SPA navigation (including the
 * create-workflow → designer auto-apply hop) but are gone on page reload.
 * This is intentional: import exists to *test* a generated template before it
 * is contributed to the public templates repo, not to persist it.
 */

import { create } from 'zustand';
import type { AutoTemplate } from '@/types/autoTemplate';
import type { TemplateDefinition } from '@/types/template';
import {
  autoTemplateToTemplateDefinition,
  extractTemplateExtras,
  type TemplateExtras,
} from '@/data/templates/templateAdapter';

export interface ImportedTemplateEntry {
  template: TemplateDefinition;
  extras: TemplateExtras;
  source: AutoTemplate;
}

interface ImportedTemplateState {
  /** Imported templates keyed by template id (metadata.id). */
  entries: Record<string, ImportedTemplateEntry>;
  /**
   * Register a validated AutoTemplate. Re-importing the same id replaces the
   * previous entry. Returns the converted template.
   */
  importTemplate: (template: AutoTemplate) => TemplateDefinition;
  removeImportedTemplate: (id: string) => void;
  clearImportedTemplates: () => void;
}

export const useImportedTemplateStore = create<ImportedTemplateState>((set) => ({
  entries: {},
  importTemplate: (source) => {
    const template = autoTemplateToTemplateDefinition(source);
    const extras = extractTemplateExtras(source);
    set((state) => ({
      entries: {
        ...state.entries,
        [template.id]: { template, extras, source },
      },
    }));
    return template;
  },
  removeImportedTemplate: (id) =>
    set((state) => {
      const { [id]: _removed, ...rest } = state.entries;
      return { entries: rest };
    }),
  clearImportedTemplates: () => set({ entries: {} }),
}));

/**
 * Extras lookup for imported templates — mirrors `getFetchedTemplateExtras`
 * from `@/services/templateService` for the imported set. Safe to call from
 * non-React code.
 */
export function getImportedTemplateExtras(templateId: string): TemplateExtras | undefined {
  return useImportedTemplateStore.getState().entries[templateId]?.extras;
}
